<template>
  <div :class="['c-sortable-header', $attrs.class]" role="row">
    <template v-for="column in visibleColumns" :key="column.key">
      <button
        v-if="isSortable(column)"
        type="button"
        role="columnheader"
        :class="[
          'c-sortable-header__cell',
          'c-sortable-header__cell--sortable',
          'tw-inline-flex',
          column.class,
        ]"
        :aria-sort="ariaSortFor(column)"
        @click="handleSort(column)"
      >
        <slot :name="column.key" :column="column">
          <span>{{ column.label }}</span>
        </slot>

        <q-icon
          :name="sortIconFor(column)"
          size="14px"
          class="c-sortable-header__icon"
        />
      </button>

      <div
        v-if="!isSortable(column)"
        role="columnheader"
        :class="['c-sortable-header__cell', 'tw-inline-flex', column.class]"
      >
        <slot :name="column.key" :column="column">
          <span>{{ column.label }}</span>
        </slot>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { toSolidIcon } from "common/defaultIcons";
import type { SortableHeaderColumn, SortableHeaderState } from "shared/types";
import { computed } from "vue";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  columns: SortableHeaderColumn[];
}>();

const modelValue = defineModel<SortableHeaderState>({ required: true });

const emit = defineEmits<{
  (event: "change", state: SortableHeaderState): void;
}>();

const visibleColumns = computed(() =>
  props.columns.filter((column) => column.hidden !== true),
);

const isSortable = (
  column: SortableHeaderColumn,
): column is SortableHeaderColumn & { sortKey: string } => {
  return column.sortKey !== undefined;
};

const handleSort = (
  column: SortableHeaderColumn & { sortKey: string },
): void => {
  const nextState: SortableHeaderState =
    modelValue.value.sort === column.sortKey
      ? {
          sort: column.sortKey,
          order: modelValue.value.order === "asc" ? "desc" : "asc",
        }
      : {
          sort: column.sortKey,
          order: "asc",
        };

  modelValue.value = nextState;
  emit("change", nextState);
};

const sortIconFor = (
  column: SortableHeaderColumn & { sortKey: string },
): string => {
  if (modelValue.value.sort !== column.sortKey) {
    return toSolidIcon("fa-sort");
  }

  return modelValue.value.order === "asc"
    ? toSolidIcon("fa-sort-up")
    : toSolidIcon("fa-sort-down");
};

const ariaSortFor = (
  column: SortableHeaderColumn & { sortKey: string },
): "ascending" | "descending" | "none" => {
  if (modelValue.value.sort !== column.sortKey) {
    return "none";
  }

  return modelValue.value.order === "asc" ? "ascending" : "descending";
};
</script>

<style lang="scss" scoped>
.c-sortable-header {
  @apply tw-min-h-[56px] tw-rounded-t-2xl tw-border-grey-100 tw-bg-white;

  // `display` is set with the `tw-inline-flex` utility in the template, not here:
  // a scoped rule outranks the `tw-hidden`/`xl:tw-inline-flex` responsive classes
  // a caller can pass via `column.class`, which would keep hidden columns visible.
  &__cell {
    color: inherit;
    font: inherit;
    font-weight: 600;
    text-align: left;
    align-items: center;

    &--sortable {
      background: transparent;
      border: none;
      padding: 0;
      cursor: pointer;

      &:hover {
        color: theme("colors.primary.DEFAULT", "currentColor");
      }

      &:focus-visible {
        outline: 2px solid theme("colors.primary.DEFAULT", "currentColor");
        outline-offset: 2px;
      }
    }
  }

  &__icon {
    margin-left: 4px;
  }
}
</style>
